/**
 * SEO Component
 * 
 * Dynamically sets meta tags and structured data for each page
 * 
 * @component
 * @example
 * ```tsx
 * <SEO
 *   title="About Us - OceanNova"
 *   description="Shipyard, industrial and infrastructure projects"
 *   canonicalPath="/about"
 * />
 * ```
 */

import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonicalPath: string;
  type?: "website" | "article";
  imageUrl?: string;
  keywords?: string[];
  structuredData?: object | object[];
}

export const SEO = ({
  title,
  description,
  canonicalPath,
  type = "website",
  imageUrl = "https://oceannova.pl/favicon-512x512.png",
  keywords = [],
  structuredData,
}: SEOProps) => {
  const canonicalUrl = `https://oceannova.pl${canonicalPath}`;
  
  const defaultKeywords = [
    "shipyard projects",
    "industrial projects",
    "steel structures",
    "assembly work",
    "port infrastructure",
    "terminal development",
    "specialist teams",
    "shipping and logistics",
  ];
  
  const allKeywords = [...defaultKeywords, ...keywords].join(", ");

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="OceanNova" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />

      {/* Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}
    </Helmet>
  );
};
